// Bottom sheet с фильтрами для экрана Транзакций. Минимально оформлено.

import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api } from '../api/client';
import type { TransactionFilters } from '../api/types';
import { SheetCta } from './SheetCta';
import { useBackButton, useMainButton } from '../lib/use-main-button';

type Props = {
  initial: TransactionFilters;
  onApply: (filters: TransactionFilters) => void;
  onClose: () => void;
};

export function FiltersSheet({ initial, onApply, onClose }: Props): JSX.Element {
  useBackButton(onClose);
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const [period, setPeriod] = useState<TransactionFilters['period']>(initial.period);
  const [kind, setKind] = useState<TransactionFilters['kind']>(initial.kind);
  const [category, setCategory] = useState<string>(initial.category ?? '');
  const [q, setQ] = useState<string>(initial.q ?? '');

  const categoriesQ = useQuery({
    queryKey: ['categories', kind],
    queryFn: () => api.categories(kind, false),
  });

  const apply = (): void => {
    onApply({
      period,
      kind,
      category: category || undefined,
      q: q || undefined,
    });
    onClose();
  };

  const reset = (): void => {
    setPeriod(undefined);
    setKind(undefined);
    setCategory('');
    setQ('');
  };

  const cta = useMainButton({
    text: 'Применить',
    onClick: apply,
  });

  return (
    <div className="sheet-backdrop" onClick={onClose} role="presentation">
      <div className="sheet" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="sheet-handle" />
        <div className="sheet-title">Фильтры</div>

        <label className="form-field">
          <span className="form-label">Период</span>
          <select
            value={period ?? ''}
            onChange={(e) => setPeriod((e.target.value || undefined) as typeof period)}
            className="form-select"
          >
            <option value="">Все время</option>
            <option value="today">Сегодня</option>
            <option value="week">7 дней</option>
            <option value="month">Этот месяц</option>
          </select>
        </label>

        <label className="form-field">
          <span className="form-label">Тип</span>
          <select
            value={kind ?? ''}
            onChange={(e) => setKind((e.target.value || undefined) as typeof kind)}
            className="form-select"
          >
            <option value="">Все</option>
            <option value="expense">Расходы</option>
            <option value="income">Доходы</option>
          </select>
        </label>

        <label className="form-field">
          <span className="form-label">Категория</span>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="form-select"
          >
            <option value="">Любая</option>
            {(categoriesQ.data ?? []).map((c) => (
              <option key={c.id} value={c.slug}>
                {c.name}
              </option>
            ))}
          </select>
        </label>

        <label className="form-field">
          <span className="form-label">Поиск по описанию</span>
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="form-input"
            placeholder="кофе, продукты, …"
          />
        </label>

        <div className="sheet-actions">
          <SheetCta {...cta} />
          <button type="button" className="btn-secondary" onClick={reset}>
            Сбросить
          </button>
          <button type="button" className="btn-secondary" onClick={onClose}>
            Закрыть
          </button>
        </div>
      </div>
    </div>
  );
}
